// Contrato de LECTURA expuesto por el BFF en `GET bff/payment-links-view`
// (wuzi-portal-bff → controllers/payment-link). El microservicio wuzi-ms-payment-links
// serializa en camelCase (ASP.NET Core), por lo que las propiedades se declaran igual.
//
// IMPORTANTE: este archivo modela el contrato REAL del BFF y es independiente del
// modelo heredado `payment-link.ts` (usado por la vista de generación/consulta previa),
// para no romper componentes existentes.

/**
 * Catálogo REAL de estados de una liga de pago: el BFF ya entrega `status_concept`
 * directamente en español (confirmado contra el GET real: "Pendiente", "Aprobado",
 * "Cancelado"), así que no hay traducción ni agrupación que hacer — se usa tal cual,
 * tanto para el filtro de Estado (hardcodeado, no derivado del BFF) como para el
 * color del badge en la tabla y en `PaymentLinkCreatedView`.
 */
export const PAYMENT_LINK_STATUSES = ['Pendiente', 'Aprobado', 'Cancelado'] as const;

export type PaymentLinkStatus = typeof PAYMENT_LINK_STATUSES[number];

interface PaymentLinkStatusColor {
  background: string;
  color: string;
  borderColor: string;
}

const NEUTRAL_STATUS_COLOR: PaymentLinkStatusColor = { background: 'neutral.200', color: 'neutral.800', borderColor: 'neutral.400' };

const PAYMENT_LINK_STATUS_COLORS: Record<PaymentLinkStatus, PaymentLinkStatusColor> = {
  Pendiente: { background: 'warning.200', color: 'warning.800', borderColor: 'warning.500' },
  Aprobado: { background: 'success.200', color: 'success.700', borderColor: 'success.400' },
  Cancelado: { background: 'destructive.200', color: 'destructive.700', borderColor: 'destructive.500' },
};

/**
 * Color del estado para el badge de la tabla y el tag de `PaymentLinkCreatedView`. Si
 * el valor no está en el catálogo conocido, se usa un color neutro (no oculta el dato,
 * `status` se sigue mostrando tal cual).
 */
export const getPaymentLinkStatusColor = (status: string): PaymentLinkStatusColor =>
  PAYMENT_LINK_STATUS_COLORS[status as PaymentLinkStatus] ?? NEUTRAL_STATUS_COLOR;

/**
 * Proyección de lectura de una liga de pago (PaymentLinkResponse del BFF).
 */
export interface PaymentLinkView {
  id: string;
  providerId: string;
  merchantId: string;
  customerId?: string;
  status: PaymentLinkStatus | string;
  token: string;
  externalLinkId?: string;
  url?: string;
  reference?: string;
  concept: string;
  amount: number;
  currencyCode: string;
  isAmountFixed: boolean;
  isReusable: boolean;
  maxUses?: number;
  activatedAt?: string;
  expireDate?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Resultado paginado genérico del BFF (PaginatedPaymentLinks → PagedResult<T>).
 * Nótese que NO usa el `PageableResponseWrapper` interno del portal: el shape lo
 * dicta el microservicio (currentPage, pageSize, totalRecords, totalPages, records).
 */
export interface PaginatedPaymentLinks {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  records: PaymentLinkView[];
}
